import { useCallback, useMemo, useState } from "react";
import { ActionIcon, Table } from "@mantine/core";
import { IconChevronRight, IconChevronDown } from "@tabler/icons-react";
import type { UseQueryResult } from "@tanstack/react-query";
import type {
  PendingWorkRow,
  PendingWorkDetail,
} from "../../types/pendingWork";
import { PendingWorkExpandedRow } from "./PendingWorkExpandedRow";
import { PendingWorkCell } from "./PendingWorkCell";

interface Props {
  row: PendingWorkRow;
  details: PendingWorkDetail[];
  detailsQuery: UseQueryResult<PendingWorkDetail[], Error>;
  columns: string[];
  overdueThresholdDays: number;
}
export function PendingWorkTableRow({
  row,
  details,
  detailsQuery,
  columns,
  overdueThresholdDays,
}: Props) {
  const [opened, setOpened] = useState(true);
  const handleToggle = useCallback(() => setOpened((current) => !current), []);
  const handleExpandClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleToggle();
    },
    [handleToggle]
  );
  const background = useMemo(
    () =>
      row.failedQc || row.priority === 2
        ? "var(--mantine-color-red-2)"
        : row.slideCount > 0
        ? "var(--mantine-color-yellow-0)"
        : row.sendoutCount > 0
        ? "var(--mantine-color-blue-1)"
        : "var(--mantine-color-green-1)",
    [row]
  );
  return (
    <>
      <Table.Tr
        bg={background}
        onClick={handleToggle}
        style={{ cursor: "pointer" }}
      >
        <Table.Td>
          <ActionIcon
            variant="subtle"
            onClick={handleExpandClick}
            aria-label={`${opened ? "Collapse" : "Expand"} pending causes for ${
              row.caseNumber
            }`}
            aria-expanded={opened}
          >
            {opened ? (
              <IconChevronDown size={17} />
            ) : (
              <IconChevronRight size={17} />
            )}
          </ActionIcon>
        </Table.Td>
        {columns.map((column) => (
          <PendingWorkCell
            key={column}
            row={row}
            column={column}
            overdueThresholdDays={overdueThresholdDays}
          />
        ))}
      </Table.Tr>
      {opened && (
        <Table.Tr>
          <Table.Td colSpan={columns.length + 1}>
            <PendingWorkExpandedRow details={details} query={detailsQuery} />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}
